import { Drawer, DrawerContent } from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import { CloseIcon, WhiteboardTemplateIcon } from '@sketchnote/icons';
import { Input } from '@sketchnote/shared-components';
import * as React from 'react';
import { useTldrawApp } from 'sn-wb-tldraw';
import { API } from 'src/api';

import { TemplateStore } from '../TemplateStore';
import { TemplateCard } from './TemplateCard';

export function TemplatesPanel() {
  const app = useTldrawApp();
  const [customTemplates, setCustomTemplates] = React.useState<any>([]);
  const [filteredTemplates, setFilteredTemplates] = React.useState<any>([]);
  const [categories, setCategories] = React.useState<any>([]);
  const state = app.useStore();
  const isTemplateLibrary = state.appState.isTemplateLibrary;
  const getCategoryName = (categoryId: string): string => {
    const category = categories?.find((category) => category._id === categoryId);

    return category?.title;
  };

  const [openSnackbar] = useSnackbar();

  React.useEffect(() => {
    const fetchTemplates = async () => {
      const { data } = await API.get('/whiteboard/template');
      const categoriesResponse = await API.get('/files/category');
      setCategories(categoriesResponse.data.data);
      setCustomTemplates(data.data.docs);
      setFilteredTemplates(data.data.docs);
    };
    fetchTemplates();
  }, []);

  const deleteTemplate = (id) => {
    API.delete('/whiteboard/template?id=' + id).then(() => {
      const newTemplates = customTemplates.filter((temp) => temp._id !== id);
      setCustomTemplates(newTemplates);
      setFilteredTemplates(newTemplates);
      openSnackbar({
        text: 'Template successfully deleted',
        snackType: 'standard',
        position: 'bottom-center',
      });
    });
  };

  const handleTemplateEdit = (template, values) => {
    app.storeCurrentTemplate();
    app.storeTemplateData(values);
    app.deleteAll();
    app.createTemplateAtPoint(template);
    app.setTemplateLibrary(false);
    app.setTemplateEditMode(true);
  };

  const handleFilter = (e) => {
    setFilteredTemplates(
      customTemplates.filter((temp) =>
        temp.title.toLowerCase().includes(e.target.value.toLowerCase())
      )
    );
  };

  const topics = React.useMemo(() => ['Product', 'Marketing', 'Financial'], []);

  return (
    <Drawer
      size="xl"
      isOpen={isTemplateLibrary}
      placement="right"
      onClose={() => app.setTemplateLibrary(false)}
    >
      <DrawerContent>
        <div
          style={{
            border: 'none',
            outline: 'none',
            pointerEvents: 'all',
            paddingBottom: '24px',
          }}
        >
          <div className="overflow-y-scroll h-full flex flex-col bg-white">
            <div className="py-16 px-24 flex justify-between items-center">
              <div className="flex gap-12 items-center">
                <WhiteboardTemplateIcon fill="#000" />
                <h3 className="font-medium text-lg">Template Library</h3>
              </div>

              <div className="flex gap-6 items-center">
                <TemplateStore categories={categories} customTemplates={customTemplates} />
                <CloseIcon
                  fill="#878A92"
                  className="cursor-pointer transform scale-90"
                  onClick={() => {
                    app.setTemplateLibrary(false);
                  }}
                />
              </div>
            </div>
            <div className="px-24 flex flex-col gap-18">
              <Input
                type="text"
                placeholder="search for any templates"
                inputClassName="h-48 py-14 pl-20"
                onChange={(e) => handleFilter(e)}
              />
              <div className="flex gap-8 items-center">
                <h5 className="text-sm font-regular">Popular Topics:</h5>
                {topics.map((topic) => (
                  <h5 key={topic} className="text-gray-600 text-sm font-medium">
                    {topic}
                  </h5>
                ))}
              </div>
              <div
                style={{
                  height: 'calc(100vh - 200px)',
                }}
                className="flex flex-col gap-12 overflow-y-scroll"
              >
                <h3 className="font-medium text-lg">We recommend</h3>
                <div
                  className="flex gap-8 flex-wrap overflow-y-scroll"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  {filteredTemplates.map((template) => (
                    <TemplateCard
                      key={template._id}
                      onClick={() => {
                        app.createTemplateAtPoint({
                          shapes: template.shapes,
                          bindings: template.bindings,
                          assets: template.assets,
                          sections: template.sections,
                        });
                        app.setTemplateLibrary(false);
                        app.zoomToFit();
                        openSnackbar({
                          text: `Template added`,
                          snackType: 'standard',
                          position: 'bottom-center',
                          duration: 1500,
                        });
                      }}
                      onEdit={handleTemplateEdit.bind(
                        null,
                        {
                          shapes: template.shapes,
                          bindings: template.bindings,
                          assets: template.assets,
                          sections: template.sections,
                        },
                        template
                      )}
                      title={template.title}
                      description={template.description}
                      image={{
                        url: template.coverImage,
                        alt: template.title,
                      }}
                      color={['blue', 'pink', 'green'][Math.floor(Math.random() * 3)]}
                      tag={getCategoryName(template.category)}
                      onDelete={() => deleteTemplate(template._id)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
